import React, { useEffect, useState } from 'react'
import { Route, Switch, Redirect, useRouteMatch, match } from 'react-router-dom'
import { omit } from 'lodash'
import omitDeep from 'omit-deep-lodash'
import { t } from '@lingui/macro'
import ErrorMessage from 'front-end/src/components/common/error/ErrorMessage'
import { useFeatureFlags } from 'front-end/src/app/hooks/useFeatureFlags'

import { notification } from '../../components/common/notifications/Notifications'
import PageHeader from '../../components/common/page/header/PageHeader'
import { TabsStatic } from '../../components/common/tabs/TabsLegacy'
import UserAdditionalInfo from './UserAdditionalInfo'
import UserBasicInfo from './UserBasicInfo'
import { UserAccessibility } from './UserAccessibility'
import './UserProfilePage.less'
import UserUpdatePassword from './UserUpdatePassword'
import HeadTitle from '../../components/common/head/HeadTitle'
import { linkBuilder } from 'shared/linkBuilder'
import {
  useOrganisationDetailsQuery,
  UserProfilePageDetailAsMeFragment,
  useUserPatchAsAdminMutation,
  useUserPatchAsSelfMutation,
  useUserProfilePageDetailAsAdminQuery,
  useUserProfilePageDetailAsMeQuery
} from './UserProfilePage.codegen'
import { loadLocaleMessages } from 'front-end/src/i18n/i18n'
import validations from 'shared/validations'
import { UserAuthentication } from './UserAuthentication'
import { OrganisationRoleEnum } from 'types/Organisation'
import { usePublicApiSession } from 'front-end/src/public-api/hooks/usePublicApiSession'

const buildPageTitle = (user: {
  display_name: any
  first_name: any
  last_name: any
}) => {
  return user.display_name
    ? user.display_name
    : `${user.first_name} ${user.last_name}`
}

const useIsEditingCurrentUser = (match) => {
  const { session } = usePublicApiSession()
  if (match.params.userId) {
    if (parseInt(match.params.userId) === session?.user.id) {
      return true
    }
    if (match.params.userId === 'me') {
      return true
    }
  }

  return false
}

export const UserProfileWrapper = (props) => {
  return useIsEditingCurrentUser(props.match) ? (
    <UserProfilePageAsSelf />
  ) : (
    <UserProfilePageAsAdmin {...props} />
  )
}

export const UserProfilePageAsSelf: React.FC = () => {
  const { data } = useUserProfilePageDetailAsMeQuery()
  if (data?.me) {
    return <UserProfilePage user={data?.me} isCurrentUser />
  }
  return null
}

export const UserProfilePageAsAdmin: React.FC = () => {
  const { params } = useRouteMatch<{ userId: string }>(
    '/user/:userId'
  ) as match<{ userId: string }>

  const userId = Number(params.userId)
  const { data } = useUserProfilePageDetailAsAdminQuery({
    variables: {
      userId
    }
  })
  if (data?.admin?.user === null) {
    return (
      <div className="User Page">
        <ErrorMessage>User Id {userId} does not exist</ErrorMessage>
      </div>
    )
  }
  if (data?.admin?.user) {
    return <UserProfilePage user={data?.admin?.user} isCurrentUser={false} />
  }
  return null
}

export const UserProfilePage = ({ user, isCurrentUser }) => {
  const { params } = useRouteMatch<{ userId: string }>(
    '/user/:userId'
  ) as match<{ userId: string }>

  const [patchUserAsAdmin] = useUserPatchAsAdminMutation()

  const [patchUserAsStudent] = useUserPatchAsSelfMutation()
  const { featureFlags } = useFeatureFlags()

  const [stateUser, setUser] =
    useState<UserProfilePageDetailAsMeFragment | null>(null)
  const [pageTitle, setPageTitle] = useState('Profile page')
  const { data, loading } = useOrganisationDetailsQuery()

  useEffect(() => {
    if (user) {
      setPageTitle(buildPageTitle(user))
      setUser(user)
    }
  }, [user])

  const handleUserForm = (formData) => {
    const newState = {
      ...stateUser,
      ...formData
    }

    setUser(newState)
  }

  if (!stateUser || loading) {
    return null
  }
  const baseUrl = isCurrentUser ? '/user/me' : `/user/${params.userId}`

  const isAllowedToEdit = () => {
    if (!data) {
      return false
    }
    const { organisation, me } = data
    return (
      (!stateUser.deleted_at && organisation.can_students_update_profile) ||
      (!stateUser.deleted_at &&
        me.organisation_role === OrganisationRoleEnum.ADMIN)
    )
  } // even Admin can not edit already archived user

  const tabList = [
    {
      caption: t`Basic information`,
      href: `${baseUrl}/basic`
    },
    {
      caption: t`Additional information`,
      href: `${baseUrl}/additional`
    }
  ]

  if (isCurrentUser) {
    tabList.push({
      caption: t`Change password`,
      href: `${baseUrl}/password`
    })
    tabList.push({
      caption: t`Accessibility`,
      href: `${baseUrl}/accessibility`
    })
    if (featureFlags?.calendars_integration) {
      tabList.push({
        caption: t`Authentication`,
        href: `${baseUrl}/authentication`
      })
    }
  }

  const submitUserForm = async () => {
    let newUser
    const omittedFields = [
      'id',
      'verified',
      'image_url',
      'password_last_updated_at',
      'manager',
      'deleted_at',
      'scimResource'
    ]
    if (stateUser.scimResource) {
      omittedFields.push('email', 'account_name')
    } else if (!featureFlags?.account_name_login) {
      omittedFields.push('account_name')
    }

    const input = omitDeep(
      omit(stateUser, omittedFields),
      '__typename'
    ) as UserProfilePageDetailAsMeFragment

    if (
      featureFlags?.account_name_login &&
      input.account_name &&
      input.account_name !== stateUser.account_name &&
      !stateUser.scimResource &&
      !input.account_name.match(validations.accountName)
    ) {
      return notification.error(
        t`Account name "${input.account_name}" is not valid`
      )
    }

    if (!stateUser.scimResource) {
      // we do not allow user to change email/account_name
      // if he is SCIM-synced user
      if (!input.email && !featureFlags?.account_name_login) {
        return notification.error(
          t`You can not clear the email. This is needed for log in.`
        )
      }

      if (!input.account_name && !input.email) {
        // user tries to clear existing account_name
        if (user.account_name) {
          return notification.error(
            t`You can't clear an account name because you don't have email`
          )
        }
        // user tries to clear email
        return notification.error(
          t`You can't clear an email because you don't have an account name`
        )
      }
      if (input.account_name === '') {
        input.account_name = null
      }
      if (input.email === '') {
        input.email = null
      }
    }

    if (isCurrentUser) {
      const result = await patchUserAsStudent({
        variables: {
          input
        }
      })
      newUser = result.data ? result.data.me.patch : null

      // load new locale messages after success user patch response
      await loadLocaleMessages({ locale: newUser?.locale })
    } else {
      const result = await patchUserAsAdmin({
        variables: {
          id: stateUser.id,
          input
        }
      })
      newUser = result.data ? result.data.admin?.user?.patch : null
    }
    if (newUser) {
      handleUserForm({ ...newUser })
      setPageTitle(buildPageTitle(newUser))
      notification.info(t`User profile successfully updated`)
    } else {
      notification.error(t`Error during user profile update`)
    }
  }

  const sameAsLogged = isCurrentUser

  return (
    <div className="User Page">
      <HeadTitle title={pageTitle} />
      <PageHeader title={pageTitle} className="PageHeader" />

      <div className="User__wrapper">
        <TabsStatic tabList={tabList}>
          <div className="User__content">
            <Switch>
              <Route
                path={`${baseUrl}/basic`}
                render={() => (
                  <UserBasicInfo
                    initialUserData={user}
                    user={stateUser}
                    handleUserForm={handleUserForm}
                    isCurrentUser={isCurrentUser}
                    submitUserForm={submitUserForm}
                    canEdit={isAllowedToEdit()}
                  />
                )}
              />
              <Route
                path={`${baseUrl}/additional`}
                render={() => (
                  <UserAdditionalInfo
                    user={stateUser}
                    handleUserForm={handleUserForm}
                    submitUserForm={submitUserForm}
                    canEdit={isAllowedToEdit()}
                  />
                )}
              />
              {sameAsLogged && (
                <Route
                  path={`${baseUrl}/password`}
                  render={() => <UserUpdatePassword user={stateUser} />}
                />
              )}
              {sameAsLogged && (
                <Route
                  path={`${baseUrl}/accessibility`}
                  render={() => <UserAccessibility />}
                />
              )}
              {sameAsLogged && featureFlags?.calendars_integration && (
                <Route
                  path={`${baseUrl}/authentication`}
                  render={() => <UserAuthentication />}
                />
              )}
              <Redirect
                to={{
                  pathname: linkBuilder.userDetail(
                    isCurrentUser ? 'me' : params.userId,
                    'basic'
                  )
                }}
                exact
              />
            </Switch>
          </div>
        </TabsStatic>
      </div>
    </div>
  )
}
